/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// Testimonials autoplay extension (kept in custom.js so theme.js can be updated safely)
(() => {
  const SPEED_FALLBACK_SECONDS = 8;
  const MIN_SPEED_SECONDS = 3;
  const wiredLists = new WeakSet();

  const setupTestimonialAutoplay = (listEl) => {
    if (!listEl || wiredLists.has(listEl) || !listEl.hasAttribute("autoplay")) {
      return;
    }

    const items = Array.from(listEl.querySelectorAll("testimonial-item"));
    if (items.length <= 1 || window.Shopify?.designMode) {
      return;
    }

    wiredLists.add(listEl);

    const parsedSpeed = Number.parseInt(listEl.getAttribute("cycle-speed") || `${SPEED_FALLBACK_SECONDS}`, 10);
    const cycleMs = Math.max(MIN_SPEED_SECONDS, Number.isNaN(parsedSpeed) ? SPEED_FALLBACK_SECONDS : parsedSpeed) * 1000;

    let intervalId = null;
    let pausedByHover = false;

    const stop = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const start = () => {
      stop();
      if (pausedByHover) {
        return;
      }
      intervalId = setInterval(() => {
        if (typeof listEl.next === "function") {
          listEl.next();
        }
      }, cycleMs);
    };

    const originalSelect = listEl.select?.bind(listEl);
    if (typeof originalSelect === "function") {
      listEl.select = async (...args) => {
        stop();
        try {
          return await originalSelect(...args);
        } finally {
          start();
        }
      };
    }

    listEl.addEventListener("mouseenter", () => {
      pausedByHover = true;
      stop();
    });

    listEl.addEventListener("mouseleave", () => {
      pausedByHover = false;
      start();
    });

    start();
  };

  const initAll = () => {
    document.querySelectorAll("testimonial-list[autoplay]").forEach(setupTestimonialAutoplay);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", initAll);
})();

// Logo list carousel autoscroll (kept in custom.js so theme.js can be updated safely)
(() => {
  const SPEED_FALLBACK_SECONDS = 8;
  const MIN_SPEED_SECONDS = 3;
  const DESKTOP_BREAKPOINT = "(min-width: 1000px)";
  const wiredLists = new WeakSet();

  const canScroll = (scrollEl) => scrollEl && scrollEl.scrollWidth > scrollEl.clientWidth + 1;

  /** Desktop scrolls .logo-list__list; mobile scrolls the .scroller wrapper. */
  const getScrollElement = (logoListEl) => {
    const list = logoListEl.querySelector(".logo-list__list");
    const scroller = logoListEl.closest(".scroller");
    if (list && canScroll(list)) {
      return list;
    }
    if (scroller && canScroll(scroller)) {
      return scroller;
    }
    return null;
  };

  const createLogoClone = (item) => {
    const clone = item.cloneNode(true);
    clone.removeAttribute("reveal");
    clone.style.opacity = "1";
    clone.setAttribute("aria-hidden", "true");
    if (clone.matches("a")) {
      clone.tabIndex = -1;
    }
    clone.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = "eager";
    });
    return clone;
  };

  /** Prepend + append clones so the strip wraps visually (last logos appear before the first). */
  const duplicateLogoItems = (logoListEl) => {
    const list = logoListEl.querySelector(".logo-list__list");
    if (!list || list.hasAttribute("data-autoplay-duplicated")) {
      return;
    }
    const originals = [...list.querySelectorAll(":scope > .logo-list__item")];
    const images = originals.flatMap((item) => [...item.querySelectorAll("img")]);
    if (images.length > 0 && images.some((img) => !img.complete)) {
      return;
    }
    for (let i = originals.length - 1; i >= 0; i -= 1) {
      list.insertBefore(createLogoClone(originals[i]), list.firstChild);
    }
    originals.forEach((item) => {
      list.appendChild(createLogoClone(item));
    });
    list.setAttribute("data-autoplay-duplicated", "");
  };

  const getOriginalLogoItems = (logoListEl) => {
    const list = logoListEl.querySelector(".logo-list__list");
    return list ? [...list.querySelectorAll(':scope > .logo-list__item:not([aria-hidden="true"])')] : [];
  };

  const getAppendedLogoClones = (logoListEl) => {
    const list = logoListEl.querySelector(".logo-list__list");
    const originals = getOriginalLogoItems(logoListEl);
    if (!list || !originals.length) {
      return [];
    }
    const clones = [...list.querySelectorAll(':scope > .logo-list__item[aria-hidden="true"]')];
    return clones.slice(-originals.length);
  };

  const getCenteredScrollLeft = (scrollEl, item) => {
    const scrollRect = scrollEl.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    return scrollEl.scrollLeft + (itemRect.left - scrollRect.left) - (scrollRect.width - itemRect.width) / 2;
  };

  const scrollLogoToCenter = (logoListEl, item, behavior = "smooth") => {
    const scrollEl = getScrollElement(logoListEl);
    if (!scrollEl || !item) {
      return;
    }
    scrollEl.scrollTo({
      left: Math.max(0, getCenteredScrollLeft(scrollEl, item)),
      behavior,
    });
  };

  /** Block horizontal swipe on the mobile scroller; autoscroll still uses scrollTo. */
  const lockScrollerSwipe = (logoListEl) => {
    const scroller = logoListEl.closest(".scroller");
    if (!scroller || scroller.hasAttribute("data-autoplay-touch-locked")) {
      return;
    }
    scroller.setAttribute("data-autoplay-touch-locked", "");
    let startX = 0;
    let startY = 0;
    scroller.addEventListener(
      "touchstart",
      (event) => {
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
      },
      { passive: true }
    );
    scroller.addEventListener(
      "touchmove",
      (event) => {
        const dx = Math.abs(event.touches[0].clientX - startX);
        const dy = Math.abs(event.touches[0].clientY - startY);
        if (dx > dy && dx > 10) {
          event.preventDefault();
        }
      },
      { passive: false }
    );
  };

  const centerLogoAtIndex = (logoListEl, behavior = "smooth") => {
    const originals = getOriginalLogoItems(logoListEl);
    const idx = Number.parseInt(logoListEl.dataset.autoplayIndex || "0", 10);
    scrollLogoToCenter(logoListEl, originals[idx], behavior);
  };

  const refreshLoopWidth = (logoListEl) => {
    duplicateLogoItems(logoListEl);
    const scrollEl = getScrollElement(logoListEl);
    if (scrollEl) {
      scrollEl.dataset.autoplayLoopWidth = String(Math.round(scrollEl.scrollWidth / 3));
      centerLogoAtIndex(logoListEl, "instant");
    }
  };

  const advanceLogoCarousel = (logoListEl) => {
    const scrollEl = getScrollElement(logoListEl);
    const list = logoListEl.querySelector(".logo-list__list");
    if (!scrollEl || !list) {
      return;
    }

    const originals = getOriginalLogoItems(logoListEl);
    if (!originals.length) {
      return;
    }

    const idx = Number.parseInt(logoListEl.dataset.autoplayIndex || "0", 10);
    const nextIdx = (idx + 1) % originals.length;
    const loopWidth = Number.parseInt(scrollEl.dataset.autoplayLoopWidth || "0", 10);

    if (nextIdx === 0 && idx === originals.length - 1 && loopWidth > 0) {
      const firstAppended = getAppendedLogoClones(logoListEl)[0];
      if (firstAppended) {
        scrollLogoToCenter(logoListEl, firstAppended);
        window.setTimeout(() => scrollLogoToCenter(logoListEl, originals[0], "instant"), 450);
      } else {
        scrollLogoToCenter(logoListEl, originals[nextIdx]);
      }
    } else {
      scrollLogoToCenter(logoListEl, originals[nextIdx]);
    }

    logoListEl.dataset.autoplayIndex = String(nextIdx);
  };

  const setupLogoListAutoplay = (logoListEl) => {
    if (!logoListEl || wiredLists.has(logoListEl) || !logoListEl.hasAttribute("autoplay")) {
      return;
    }

    if (logoListEl.querySelectorAll(".logo-list__item").length <= 1 || window.Shopify?.designMode) {
      return;
    }

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    wiredLists.add(logoListEl);
    lockScrollerSwipe(logoListEl);

    const parsedSpeed = Number.parseInt(logoListEl.getAttribute("cycle-speed") || `${SPEED_FALLBACK_SECONDS}`, 10);
    const cycleMs = Math.max(MIN_SPEED_SECONDS, Number.isNaN(parsedSpeed) ? SPEED_FALLBACK_SECONDS : parsedSpeed) * 1000;

    let intervalId = null;
    let pausedByHover = false;

    const stop = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const start = () => {
      stop();
      if (pausedByHover || !getScrollElement(logoListEl)) {
        return;
      }
      intervalId = setInterval(() => advanceLogoCarousel(logoListEl), cycleMs);
    };

    const scheduleStart = () => {
      requestAnimationFrame(() => {
        refreshLoopWidth(logoListEl);
        start();
      });
    };

    logoListEl.addEventListener("mouseenter", () => {
      pausedByHover = true;
      stop();
    });

    logoListEl.addEventListener("mouseleave", () => {
      pausedByHover = false;
      scheduleStart();
    });

    const resizeObserver = new ResizeObserver(scheduleStart);
    resizeObserver.observe(logoListEl);
    const list = logoListEl.querySelector(".logo-list__list");
    if (list) {
      resizeObserver.observe(list);
    }
    const scroller = logoListEl.closest(".scroller");
    if (scroller) {
      resizeObserver.observe(scroller);
    }

    logoListEl.querySelectorAll("img").forEach((img) => {
      if (!img.complete) {
        img.addEventListener("load", scheduleStart, { once: true });
      }
    });

    window.addEventListener("load", scheduleStart, { once: true });
    window.matchMedia(DESKTOP_BREAKPOINT).addEventListener("change", scheduleStart);
    scheduleStart();
  };

  const initAll = () => {
    document.querySelectorAll("logo-list[autoplay]").forEach(setupLogoListAutoplay);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", initAll);
})();

/**
 * Uploadly (app block): show only when a new logo upload is required.
 * Teamwear: `[data-bulk-sponsor-panel]` radio `value="new"`.
 * Workwear: `[data-bulk-logo-fee-panel]` checkbox `[data-bulk-logo-fee-cb]`.
 */
(function () {
  const SEL_GATE = "[data-uploadly-sponsor-gate]";
  const SEL_PANEL = "[data-bulk-sponsor-panel]";
  const SEL_NEW_RADIO = 'input[data-bulk-sponsor-radio][value="new"]';
  const SEL_WORKWEAR_PANEL = "[data-bulk-logo-fee-panel]";
  const SEL_WORKWEAR_CB = "input[data-bulk-logo-fee-cb]";

  function isUploadlyNotesPropertyName(name) {
    const match = String(name || "").match(/^properties\[(.+)\]$/);
    return Boolean(match && /^notes?:?(?:\s*\d+)?$/i.test(match[1].trim()));
  }

  function sanitizeSponsorUploadlyNotes(gate) {
    gate
      .querySelectorAll(
        'textarea[class*="textAreaField_"], textarea[id^="textArea_"], [data-ph-textarea], [name^="properties[Notes:"]'
      )
      .forEach((control) => {
        if (!(control instanceof HTMLInputElement || control instanceof HTMLTextAreaElement)) {
          return;
        }
        const isNotesControl =
          control.matches('textarea[class*="textAreaField_"], textarea[id^="textArea_"], [data-ph-textarea]') ||
          isUploadlyNotesPropertyName(control.name);
        if (!isNotesControl) {
          return;
        }
        if (control.value) control.value = "";
        if (control.name) control.removeAttribute("name");
        if (!control.disabled) control.disabled = true;
        control.hidden = true;
        control.setAttribute("aria-hidden", "true");
        control.setAttribute("tabindex", "-1");
        control.setAttribute("data-kitwear-uploadly-notes-control", "");
      });

    gate.querySelectorAll("label, p, span").forEach((label) => {
      if (/^notes:\s*$/i.test((label.textContent || "").trim())) {
        label.setAttribute("data-kitwear-uploadly-notes-label", "");
        label.hidden = true;
      }
    });
  }

  function observeSponsorUploadlyDom(gate) {
    sanitizeSponsorUploadlyNotes(gate);
    if (gate.dataset.uploadlySponsorDomObserved === "true" || typeof MutationObserver === "undefined") {
      return;
    }
    gate.dataset.uploadlySponsorDomObserved = "true";
    const observer = new MutationObserver(() => sanitizeSponsorUploadlyNotes(gate));
    observer.observe(gate, { childList: true, subtree: true, attributes: true, attributeFilter: ["name"] });
  }

  /**
   * @param {HTMLElement} gate
   */
  function bindGate(gate) {
    observeSponsorUploadlyDom(gate);
    if (gate.getAttribute("data-uploadly-force-hidden") === "true") {
      gate.setAttribute("hidden", "");
      return;
    }
    const form = gate.closest(".product-form");
    if (!form) {
      gate.removeAttribute("hidden");
      return;
    }
    const hasBulkTable = Boolean(form.querySelector("[data-bulk-order-table]"));
    const panel = form.querySelector(SEL_PANEL);
    const workwearPanel = form.querySelector(SEL_WORKWEAR_PANEL);
    if (!panel && !workwearPanel) {
      if (hasBulkTable) {
        gate.setAttribute("hidden", "");
      } else {
        gate.removeAttribute("hidden");
      }
      return;
    }

    const sync = () => {
      const newRadio = panel ? panel.querySelector(SEL_NEW_RADIO) : null;
      const workwearCb = workwearPanel ? workwearPanel.querySelector(SEL_WORKWEAR_CB) : null;
      const show = Boolean((newRadio && newRadio.checked) || (workwearCb && workwearCb.checked));
      if (show) {
        gate.removeAttribute("hidden");
      } else {
        gate.setAttribute("hidden", "");
      }
    };

    if (panel) {
      panel.addEventListener("change", (e) => {
        const t = /** @type {HTMLElement} */ (e.target);
        if (t.matches("[data-bulk-sponsor-radio]")) {
          sync();
        }
      });
    }
    if (workwearPanel) {
      workwearPanel.addEventListener("change", (e) => {
        const t = /** @type {HTMLElement} */ (e.target);
        if (t.matches(SEL_WORKWEAR_CB)) {
          sync();
        }
      });
    }
    sync();
  }

  function initUploadlySponsorGate() {
    document.querySelectorAll(SEL_GATE).forEach((el) => {
      if (!(el instanceof HTMLElement)) {
        return;
      }
      if (el.dataset.uploadlySponsorGateBound === "true") {
        return;
      }
      el.dataset.uploadlySponsorGateBound = "true";
      bindGate(el);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initUploadlySponsorGate);
  } else {
    initUploadlySponsorGate();
  }
  document.addEventListener("shopify:section:load", initUploadlySponsorGate);
})();

/** Keep one Uploadly artwork preview per Kitwear garment on both cart surfaces. */
(function () {
  const ROW_SELECTOR =
    'tr.line-item[data-kitwear-bulk-group], line-item.line-item[data-kitwear-bulk-group]';
  const UPLOAD_IMAGE_SELECTOR =
    'img[id^="cartImage"], img[src*="ucarecdn"], img[src*="uploadcare"], img[src*="uploadly"], img[src*="com-uploadly"], img[src*="upstorage"]';

  function dedupeKitwearUploadPreviews() {
    document.querySelectorAll(ROW_SELECTOR).forEach((row) => {
      const images = Array.from(row.querySelectorAll(UPLOAD_IMAGE_SELECTOR)).filter(
        (img) =>
          img instanceof HTMLImageElement &&
          !img.classList.contains("line-item__image") &&
          !img.classList.contains("KitwearCartCustomization__thumb")
      );
      images.forEach((img, index) => {
        const container = img.closest("li") || img;
        if (!(container instanceof HTMLElement)) return;
        if (index === 0) {
          container.removeAttribute("data-kitwear-upload-preview-duplicate");
        } else {
          container.setAttribute("data-kitwear-upload-preview-duplicate", "");
        }
      });
    });
  }

  function initKitwearUploadPreviewDedupe() {
    dedupeKitwearUploadPreviews();
    if (document.documentElement.dataset.kitwearUploadPreviewDedupeBound === "true") return;
    document.documentElement.dataset.kitwearUploadPreviewDedupeBound = "true";
    if (typeof MutationObserver === "undefined") return;
    const observer = new MutationObserver(dedupeKitwearUploadPreviews);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initKitwearUploadPreviewDedupe);
  } else {
    initKitwearUploadPreviewDedupe();
  }
  document.addEventListener("shopify:section:load", dedupeKitwearUploadPreviews);
})();
